import { TRACKABLE_EVENTS, trackEventBudget } from 'helpers/trackerHelpers';
import { tagFromProposal } from 'routes/BudgetModule/components/BudgetProposal/helpers/eventHelpers';
import { AtterrissageProjectionUpdateFieldT } from 'routes/BudgetModule/components/BudgetProposal/Resource/Projection/helpers/types';
import { BudgetProposalT } from 'routes/BudgetModule/config/types';

type TrackEvent = {
    onRowGroupOpenedTrackEvent?: string;
    onGroupChangedTrackEvent: string;
    onFilteredTrackEvent?: string;
};

/**
 * Return a tracking function for a given projection_detail field updated (atterrissage case).
 * Function is composed to prevent passing many arguments down the tree.
 * @param args an object containing the context of the tracking function
 * @returns a function taking a projection details field as argument
 */
export const getProjectionDetailsUpdateTracker =
    ({
        isDaf,
        resourceSoftwareIdNameMapping,
        budgetProposal,
    }: {
        isDaf: boolean;
        resourceSoftwareIdNameMapping: Record<string, string>;
        budgetProposal: BudgetProposalT;
    }) =>
    (field: AtterrissageProjectionUpdateFieldT['field']) => {
        if (isDaf) {
            trackEventBudget(
                TRACKABLE_EVENTS.budget.budgetOverview.projections.atterrissage[
                    `${field}Updated`
                ],
            );
        } else {
            // when in resource view, we only have one resource
            const resourceName = Object.values(
                resourceSoftwareIdNameMapping,
            )[0];
            trackEventBudget(
                TRACKABLE_EVENTS.budget.budgetProposal.projection.atterrissage[
                    `${field}Updated`
                ],
                {
                    ...tagFromProposal(budgetProposal),
                    resource: resourceName,
                },
            );
        }
    };

/**
 * Return a tracking function for a projection_detail amount field updated (pluriannual projection case).
 * @param args an object containing the context of the tracking function (isDaf or not)
 * @returns a function returning the appropriate trackers
 */
export const getPluriannualProjectionDetailsUpdateTracker =
    (isDaf: boolean) => () => {
        if (isDaf) {
            trackEventBudget(
                TRACKABLE_EVENTS.budget.budgetOverview.projections.pluriannual
                    .amountUpdated,
            );
        } else {
            trackEventBudget(
                TRACKABLE_EVENTS.budget.budgetProposal.projection.pluriannual
                    .amountUpdated,
            );
        }
    };

/**
 * Return a dict of TrackEvent ready to be triggered within the ProjectionGrid component : atterrissage case.
 * @param isDaf : a boolean. TrackEvents are not the same in the Daf View.
 * @returns a dict of track events.
 */
export const getTrackEventsForAtterrissage = (isDaf: boolean): TrackEvent =>
    isDaf
        ? {
              onRowGroupOpenedTrackEvent:
                  TRACKABLE_EVENTS.budget.budgetOverview.projections
                      .atterrissage.resourceToggled,
              onFilteredTrackEvent:
                  TRACKABLE_EVENTS.budget.budgetOverview.projections
                      .atterrissage.filtered,
              onGroupChangedTrackEvent:
                  TRACKABLE_EVENTS.budget.budgetOverview.projections
                      .atterrissage.groupingChanged,
          }
        : {
              onGroupChangedTrackEvent:
                  TRACKABLE_EVENTS.budget.budgetProposal.projection.atterrissage
                      .groupingChanged,
          };

/**
 * Return a dict of TrackEvent ready to be triggered within the ProjectionGrid component: pluriannual case.
 * @param isDaf : a boolean. TrackEvents are not the same in the Daf View.
 * @returns a dict of track events.
 */
export const getTrackEventsForPluriannualProjections = (
    isDaf: boolean,
): TrackEvent =>
    isDaf
        ? {
              onFilteredTrackEvent:
                  TRACKABLE_EVENTS.budget.budgetOverview.projections.pluriannual
                      .filtered,
              onGroupChangedTrackEvent:
                  TRACKABLE_EVENTS.budget.budgetOverview.projections.pluriannual
                      .groupingChanged,
          }
        : {
              onGroupChangedTrackEvent:
                  TRACKABLE_EVENTS.budget.budgetProposal.projection.pluriannual
                      .groupingChanged,
          };
